import { useEffect, useState } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Upload, CreditCard, Banknote, Smartphone, Copy, Check, Home, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import KpayPayNow from '../components/KpayPayNow';
import { apiFetchJson } from '../lib/api';
import { formatPrice, brandifyText } from '../lib/utils';
import { trackPageView } from '../lib/analytics';
import { fetchProducts } from '../data/products';

interface OrderItem {
  productId?: number;
  name?: string;
  quantity?: number;
  unitPrice?: number;
  subtotal?: number;
  packageType?: string;
}

interface OrderStatusResponse {
  orderNum: string;
  status: string;
  finalPrice?: number;
  items?: OrderItem[];
  isConfirmed: boolean;
  createdAt: number;
  confirmedAt?: number;
}

interface SiteSettings {
  fps_id?: string;
  company_name?: string;
  business_phone?: string;
}

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams<{ orderNum?: string }>();
  const orderNum = params.orderNum || searchParams.get('orderNum');
  const totalFromQuery = searchParams.get('total');

  const [order, setOrder] = useState<OrderStatusResponse | null>(null);
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [productImages, setProductImages] = useState<Record<number, string>>({});
  // KPay 線上支付：returnUrl 帶 ?kpay=1，輪詢至 webhook／查詢補單確認付款
  const [kpayPaid, setKpayPaid] = useState(false);
  // T04（審計 L5/D4）：KPay 回跳輪詢 120s 未確認——任何情況唔出 FPS/PayMe 教學（防雙重付款）
  const [kpayTimedOut, setKpayTimedOut] = useState(false);
  const isKpayReturn = searchParams.get('kpay') === '1';

  const queryTotal = totalFromQuery ? parseInt(totalFromQuery, 10) : 0;
  // T16（審計 BUG-10d）：只信 API 回的 finalPrice——URL ?total= 可偽造，僅 loading 期 placeholder
  const displayTotal = order?.finalPrice || queryTotal || 0;

  useEffect(() => {
    trackPageView('/order/success');
    apiFetchJson<SiteSettings>('/public/settings')
      .then((res) => {
        if (res.success && res.data) {
          setSettings(res.data);
        }
      })
      .catch(() => {});
    if (!orderNum) {
      setLoading(false);
      return;
    }
    apiFetchJson<OrderStatusResponse>(`/public/orders/${orderNum}/status`)
      .then((res) => {
        if (res.success && res.data) {
          setOrder(res.data);
          // 修復（2026-10-14 DM1518 case）：KPay 已付款嘅單，從 WhatsApp 按鈕入嚟（無 ?kpay=1）
          // 之前永遠顯示「待付款」——以真實狀態為準
          const st = String((res.data as { status?: string }).status || '');
          if (['payment_confirmed', 'processing', 'shipped', 'completed'].includes(st)) {
            setKpayPaid(true);
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // 產品縮圖對照表（訂單 items 只存 productId，圖要從產品目錄 lookup）
    fetchProducts()
      .then((all) => {
        const map: Record<number, string> = {};
        all.forEach((p) => { if (p.id && p.imageUrl) map[Number(p.id)] = String(p.imageUrl); });
        setProductImages(map);
      })
      .catch(() => {});
  }, [orderNum]);

  useEffect(() => {
    if (!isKpayReturn || !orderNum) return;
    let cancelled = false;
    let tries = 0;
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      tries += 1;
      try {
        const res = await apiFetchJson<{ success: boolean; data?: { paid: boolean } }>(
          `/public/orders/${orderNum}/kpay/status`
        );
        if (res.success && res.data?.paid) {
          if (!cancelled) setKpayPaid(true);
          return;
        }
      } catch {
        // 輪詢失敗照等下一次
      }
      if (tries >= 40) { if (!cancelled) setKpayTimedOut(true); return; } // T04：120s 超時
      setTimeout(tick, 3000);
    };
    const t = setTimeout(tick, 2000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isKpayReturn, orderNum]);

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!orderNum) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">找不到訂單資訊</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-12 md:py-20">
        <div className="max-w-xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${kpayPaid ? 'bg-green-600' : 'bg-green-100'}`}>
              <CheckCircle size={48} className={kpayPaid ? 'text-white' : 'text-green-600'} />
            </div>
            <h1 className="text-3xl font-bold mb-2">{kpayPaid ? '付款已確認' : (isKpayReturn && !kpayTimedOut) ? '正在確認信用卡付款…' : '訂單已確認'}</h1>
            <p className="text-stone-600">
              {kpayPaid
                ? '線上付款成功，我們會盡快安排出貨。🌶️'
                : (isKpayReturn && !kpayTimedOut)
                  ? '付款確認中，請稍候——唔使重複付款。'
                  : '感謝你的訂單，請按照以下方式付款。'}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">載入中...</div>
          ) : (
            <div className="space-y-6">
              <div className="bg-stone-50 rounded-2xl p-6">
                <div className="flex justify-between items-center py-3 border-b border-stone-200">
                  <span className="text-stone-600">訂單編號</span>
                  <span className="font-bold text-xl">#{orderNum}</span>
                </div>

                {/* 訂購產品：每個產品一行（縮圖 + 名稱 + 單價×數量 + 小計） */}
                {order?.items && order.items.length > 0 && (
                  <div className="py-3 border-b border-stone-200">
                    <span className="text-stone-600 block mb-3">訂購產品</span>
                    <div className="space-y-3">
                      {order.items.map((item, idx) => {
                        const qty = item.quantity || 1;
                        const name = brandifyText(String(
                          item.name ||
                          (item.packageType ? (item.packageType === '2-dish-1-soup' ? '2餸1湯' : '3餸1湯') : `商品 #${item.productId ?? ''}`)
                        ));
                        const img = item.productId != null ? productImages[Number(item.productId)] : undefined;
                        return (
                          <div key={idx} className="flex items-center gap-3">
                            {img ? (
                              <img src={img} alt={name} className="w-12 aspect-square rounded-lg object-cover flex-shrink-0 bg-white" />
                            ) : (
                              <div className="w-12 aspect-square rounded-lg bg-white border border-stone-200 flex items-center justify-center text-lg flex-shrink-0">🌶️</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold text-stone-800 break-words">{name}</div>
                              {item.unitPrice != null && (
                                <div className="text-xs text-stone-500 mt-0.5">{formatPrice(item.unitPrice)} × {qty}</div>
                              )}
                            </div>
                            <div className="text-sm font-bold text-stone-800 whitespace-nowrap">
                              {formatPrice(item.subtotal ?? ((item.unitPrice || 0) * qty))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center py-3 border-b border-stone-200">
                  <span className="text-stone-600">總金額</span>
                  <span className="font-bold text-2xl text-x1493-primary">
                    {displayTotal ? formatPrice(displayTotal) : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-stone-600">狀態</span>
                  {kpayPaid ? (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold">
                      已付款（線上支付）
                    </span>
                  ) : (
                    <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-bold">
                      待付款
                    </span>
                  )}
                </div>
              </div>

              {!kpayPaid && isKpayReturn && !kpayTimedOut && (
              <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-stone-300 border-t-x1493-primary rounded-full mx-auto mb-3" />
                <p className="text-stone-600">正在確認信用卡付款，請耐心等候。</p>
              </div>
              )}
              {!kpayPaid && isKpayReturn && kpayTimedOut && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
                <p className="font-bold text-amber-900 mb-2">未能自動確認付款</p>
                <p className="text-stone-600 text-sm">如已扣賬，請透過 WhatsApp 聯絡客服跟進（報上訂單編號 #{orderNum}）——唔使重複付款。</p>
              </div>
              )}
              {!kpayPaid && !isKpayReturn && (
                <KpayPayNow
                  orderNum={orderNum}
                  fallback={
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                        <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                          <CreditCard size={22} />
                          付款資訊
                        </h3>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <Banknote size={20} className="text-amber-700 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="font-bold text-stone-800">FPS 轉數快 / PayMe</p>
                              <div className="flex items-center gap-2">
                                <p className="text-xl font-mono font-bold">{settings.fps_id || '—'}</p>
                                <button
                                  onClick={() => settings.fps_id && handleCopy(settings.fps_id, 'fps')}
                                  className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition"
                                >
                                  {copiedField === 'fps' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Smartphone size={20} className="text-amber-700 mt-1 flex-shrink-0" />
                            <div>
                              <p className="font-bold text-stone-800">戶口名稱</p>
                              <p>{settings.company_name || '—'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                        <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
                          <Upload size={22} />
                          上傳付款憑證
                        </h3>
                        <p className="text-stone-600 mb-4 text-sm">付款後請上傳憑證，我們會盡快確認。</p>
                        <button
                          onClick={() => navigate(`/payment-proof/${orderNum}`)}
                          className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2"
                        >
                          <Upload size={20} />
                          上傳付款記錄
                        </button>
                      </div>
                    </>
                  }
                />
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/"
                  className="flex-1 py-3 rounded-xl font-bold border-2 border-stone-200 text-stone-700 hover:bg-stone-100 transition flex items-center justify-center gap-2"
                >
                  <Home size={18} />
                  返回首頁
                </Link>
                <button
                  onClick={() => navigate(`/order/${orderNum}`)}
                  className="flex-1 py-3 rounded-xl font-bold border-2 border-stone-200 text-stone-700 hover:bg-stone-100 transition flex items-center justify-center gap-2"
                >
                  <Search size={18} />
                  查看訂單
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
